import React, { useCallback, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { MasterCampaignPlan } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignStub } from 'api/interfaces/MasterCampaignStub'
import { useGetDomainsQuery } from 'api/rtkQueryApi/platform/domainsApi'
import { RootState } from 'app/rootReducer'
import { MediaPlanRef } from 'features/planner/pages/MasterCampaignPlanPage/components/MediaPlan'
import { MultiTabPdfService, MultiTabPdfProgress } from 'services/multiTabPdfService'
import { TabPdfGenerationContext } from 'services/tabPdfGenerators'

import { startMultiTabPdfGeneration, updateMultiTabPdfProgress, completeMultiTabPdfGeneration, setMultiTabPdfError, clearMultiTabPdfState, selectMultiTabPdfGenerationState, selectMultiTabPdfProgress } from '../features/planner/pages/MasterCampaignPlanPage/pdfViewSlice'

export interface UseCombinedPdfGenerationProps {
  masterCampaignPlan: MasterCampaignPlan
  masterCampaignStub: MasterCampaignStub
  onTabChange: (tabId: string) => void
  tabRefs: {
    ganttChartRef?: React.RefObject<HTMLDivElement>
    marketAnalysisPrintRef?: React.RefObject<HTMLDivElement>
    projectionRefs?: {
      deliveryMetricsRef?: React.RefObject<HTMLDivElement>
      connectedTvRef?: React.RefObject<HTMLDivElement>
      internetDisplayRef?: React.RefObject<HTMLDivElement>
      targetedHouseholdsRef?: React.RefObject<HTMLDivElement>
      billboardLocationsRef?: React.RefObject<HTMLDivElement>
    }
    projectionMapRefs?: {
      targetedHouseholdMapRef?: React.RefObject<any>
      billboardMapRef?: React.RefObject<any>
    }
    mediaplanRef?: React.RefObject<MediaPlanRef>
  }
}

export interface UseCombinedPdfGenerationResult {
  progress: MultiTabPdfProgress | null
  error: string | null
  generateAllClientPdfs: (isStaffPdf: boolean) => Promise<void>
  clearError: () => void
}

export const useCombinedPdfGeneration = ({ masterCampaignPlan, masterCampaignStub, onTabChange, tabRefs }: UseCombinedPdfGenerationProps): UseCombinedPdfGenerationResult => {
  const dispatch = useDispatch()
  const { currentOrganization, currentAccount } = useSelector((state: RootState) => state.app)
  const { data: domains } = useGetDomainsQuery()

  const planId = masterCampaignPlan.id
  const { isGenerating } = useSelector((state: RootState) => selectMultiTabPdfGenerationState(state, planId))
  const progress = useSelector((state: RootState) => selectMultiTabPdfProgress(state, planId))
  const generationState = useSelector((state: RootState) => state.pdfView.multiTabGeneration[planId])
  const error = generationState?.error || null

  const generateAllClientPdfs = useCallback(
    async (isStaffPdf: boolean) => {
      if (isGenerating || !currentAccount) return

      dispatch(startMultiTabPdfGeneration({ planId, isStaffPdf }))

      const context: TabPdfGenerationContext = {
        masterCampaignPlan,
        masterCampaignStub,
        organizationName: currentOrganization?.name || '',
        accountName: currentAccount.name || '',
        domains: domains || []
      }

      const pdfService = new MultiTabPdfService(isStaffPdf, (progress: MultiTabPdfProgress) => {
        dispatch(updateMultiTabPdfProgress({ planId, progress }))
      })

      try {
        await pdfService.generateAllClientPdfs(context, onTabChange, tabRefs)
        dispatch(completeMultiTabPdfGeneration(planId))
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        dispatch(setMultiTabPdfError({ planId, error: errorMessage }))
      }
    },
    [isGenerating, currentAccount, masterCampaignPlan, masterCampaignStub, domains, onTabChange, tabRefs, dispatch, planId]
  )

  const clearError = useCallback(() => {
    dispatch(clearMultiTabPdfState(planId))
  }, [dispatch, planId])

  useEffect(() => {
    return () => {
      if (generationState?.isGenerating) {
        dispatch(completeMultiTabPdfGeneration(planId))
      }
    }
  }, [dispatch, planId, generationState?.isGenerating])

  return {
    progress,
    error,
    generateAllClientPdfs,
    clearError
  }
}
